import moment from 'moment';
import React from 'react';
import { Col, Fade, Row } from 'reactstrap';
import QuestionnaireReportComponent from '../../../components/Cards/Report/Questionnaire';
import {
  QuestionnaireContainer,
  QuestionnaireGroupSection,
  QuestionnaireQuestionField,
} from '../../../components/Cards/Report/Questionnaire/Sections/Questionnaire';
import { usePatient } from '../../../contexts/patient';
import { usePractitioner } from '../../../contexts/practitioner';
import { useQuestionnaireReponse } from '../../../contexts/questionnaireResponse';
import {
  IQuestionnaireResponseItemAnswerComponent,
  IQuestionnaireResponseItemAnswerTableComponent,
  IQuestionnaireResponseItemGroupComponent,
  IQuestionnaireResponseItemTableGroupComponent,
  QuestionnaireResponseAnswerComponent,
  QuestionnaireResponseItemComponent,
} from '../../../modules/unsw-fhir/QuestionnaireResponse';
import {
  isCodingAnswerComponent,
  isDateAnswerComponent,
  isDecimalAnswerComponent,
  isIntegerAnswerComponent,
  isQuestionnaireResponseItemAnswer,
  isQuestionnaireResponseItemGroup,
  isQuestionnaireResponseItemTableGroup,
  isStringAnswerComponent,
} from '../../../modules/unsw-fhir/QuestionnaireResponse/typeGuards';
import QuestionnaireResponseProvider from '../../Providers/QuestionnnaireResponse';
import './index.css';

export const answerItemToString = (
  item: QuestionnaireResponseAnswerComponent,
): string => {
  if (isStringAnswerComponent(item)) {
    return item.valueString;
  }
  if (isCodingAnswerComponent(item)) {
    return item.valueCoding.display || '-1';
  }
  if (isDateAnswerComponent(item)) {
    return moment(item.valueDate).format('DD-MM-YYYY');
  }
  if (isIntegerAnswerComponent(item)) {
    return item.valueInteger.toString();
  }
  if (isDecimalAnswerComponent(item)) {
    return item.valueDecimal.toString();
  }
  throw new Error(`Could not convert answer Item to string`);
};

const QuestionnaireItem = (props: {
  item?: IQuestionnaireResponseItemAnswerComponent;
}) => {
  if (!props.item) return null;
  return (
    <QuestionnaireQuestionField
      question={props.item.text || ''}
      value={props.item.answer.map(answer => answerItemToString(answer))}
    />
  );
};

const QuestionnaireTableItemRow = (props: {
  item: IQuestionnaireResponseItemAnswerComponent;
}) => {
  const [value] = props.item.answer.map(answer => answerItemToString(answer));
  return (
    <span>
      {props.item.text}: {value || 'None'}
    </span>
  );
};

const QuestionnaireTableItem = (props: {
  item?: IQuestionnaireResponseItemAnswerTableComponent;
}) => {
  if (!props.item) return null;
  const value = props.item.answer.map(answer => answerItemToString(answer));
  const rows = props.item.item?.filter(isQuestionnaireResponseItemAnswer);
  return (
    <>
      <span className="col-auto pt-2">
        <span className="mb-0 mr-2">{props.item.text}:</span>
      </span>
      <span>
        {Array.isArray(value) ? (
          value.map((v, index) => <span key={index}>{v}</span>)
        ) : (
          <span>{value}</span>
        )}
      </span>
      {rows ? (
        rows.map((item, i) => (
          <QuestionnaireTableItemRow
            key={`${props.item?.linkId}-${i}`}
            item={item}
          />
        ))
      ) : (
        <span />
      )}
    </>
  );
};

const QuestionnaireTableGroupSection = (props: {
  item: IQuestionnaireResponseItemTableGroupComponent;
}) => {
  if (!props.item) return null;
  return (
    <>
      <Row
        // noGutters
        className="d-flex justify-content-start qtnnaire-report-item">
        <Col className="col-auto pt-2">
          <strong className="mb-0 mr-2">{props.item.text}:</strong>
        </Col>
      </Row>
      <Row className="border-bottom">
        <Col className="tc-container-view">
          {props.item.item.map(item => (
            <QuestionnaireTableItem key={item.linkId} item={item} />
          ))}
        </Col>
      </Row>
    </>
  );
};

const Section = (props: {
  item?: IQuestionnaireResponseItemGroupComponent;
}) => {
  if (!props.item) return null;

  const groups = props.item.item.filter(
    (item): item is IQuestionnaireResponseItemGroupComponent =>
      isQuestionnaireResponseItemGroup(item) &&
      !isQuestionnaireResponseItemTableGroup(item),
  );

  const questionnaireItems = props.item.item.filter(
    item =>
      isQuestionnaireResponseItemAnswer(item) ||
      isQuestionnaireResponseItemTableGroup(item),
  );

  return (
    <>
      <QuestionnaireGroupSection
        linkId={props.item.linkId}
        title={props.item.text || ''}>
        {questionnaireItems.map(item => {
          if (isQuestionnaireResponseItemAnswer(item)) {
            return <QuestionnaireItem key={item.linkId} item={item} />;
          }
          if (isQuestionnaireResponseItemTableGroup(item)) {
            return (
              <QuestionnaireTableGroupSection key={item.linkId} item={item} />
            );
          }
          return null;
        })}
      </QuestionnaireGroupSection>
      {groups.map(group => (
        <Section key={group.linkId} item={group} />
      ))}
    </>
  );
};

export const Item = (props: { item?: QuestionnaireResponseItemComponent }) => {
  if (!props.item) return null;
  return isQuestionnaireResponseItemGroup(props.item) ? (
    <Section item={props.item} />
  ) : (
    <QuestionnaireItem />
  );
};

const QuestionnaireResponse = (props: { tabId?: string }) => {
  const { questionnaireResponse, questionnaire } = useQuestionnaireReponse();
  const { getDisplayName: getPractitionerDisplayName } = usePractitioner();
  const { getDisplayName: getPatientDisplayName } = usePatient();
  if (!questionnaireResponse || !questionnaire) return null;
  const { title = '' } = questionnaire;
  return (
    <QuestionnaireReportComponent
      practitionerDisplayName={getPractitionerDisplayName()}
      patientDisplayName={getPatientDisplayName()}
      onComplete={() => {}}>
      <QuestionnaireContainer editable tabId="0" title={title}>
        {questionnaireResponse.item.map(item => (
          <Item key={item.linkId} item={item} />
        ))}
      </QuestionnaireContainer>
    </QuestionnaireReportComponent>
  );
};
const QuestionnaireResponseReport = (props: {
  questionnaireResponseId: string;
  tabId?: string;
}) => {
  return (
    <QuestionnaireResponseProvider
      questionnaireResponseId={props.questionnaireResponseId}>
      <Fade>
        <QuestionnaireResponse />
      </Fade>
    </QuestionnaireResponseProvider>
  );
};

export default QuestionnaireResponseReport;
